// ============================================================================
// CONTRAT « MODE D'AUTHENTIFICATION » — la réponse de GET /auth/mode.
//
// ⚠️ POURQUOI CE FICHIER EXISTE : cette route est la PREMIÈRE chose que tout SPA Orgazone appelle au boot ;
// c'est elle qui lui dit s'il doit exiger une session et quel moyen de connexion proposer. Elle était
// écrite à la main dans chaque app, et avait donc trois formes pour un même nom (audit 2026-08-02) :
//   • store   : absente             → le SPA n'avait AUCUN moyen de savoir, il devinait
//   • console : { oidc }            → pas de notion d'armement
//   • edi     : { oidc, armed }     → la forme complète
// Une divergence de contrat est SILENCIEUSE : rien ne casse au build, l'app se comporte juste
// différemment. C'est exactement le cas où la mutualisation vaut dès la 1ʳᵉ occurrence (SPEC §24.3bis).
//
// Ce module est PUR (zéro import) : il est consommé par le serveur (plugin Fastify) ET par le navigateur
// (`./client`), sans qu'aucun des deux ne tire les dépendances de l'autre.
// ============================================================================

/** Ce qu'un service Orgazone annonce de son authentification. Forme IDENTIQUE pour toutes les apps. */
export interface AuthMode {
  /** Un fournisseur d'identité (SSO OIDC) est configuré → proposer « Se connecter avec Orgazone ». */
  oidc: boolean;
  /**
   * L'authentification est ARMÉE : au moins un moyen de se connecter existe (SSO ou compte local), donc
   * l'API est protégée et le SPA doit exiger une session. `false` = mode OUVERT, cas bêta d'un service
   * pas encore configuré : accès direct assumé, jamais un faux écran de login qui ne mènerait nulle part.
   */
  armed: boolean;
}

/**
 * Repli quand le mode n'a PAS pu être déterminé (service injoignable, réponse illisible).
 * ⚠️ FAIL-CLOSED délibéré : on ferme (`armed: true`). Retomber en « ouvert » ferait monter l'application
 * complète au SPA, dont le premier appel protégé prendrait un 401 → retour au login → même échec → boucle.
 * C'est le bug réel corrigé sur l'EDI le 2026-08-01. Fermer est aussi le choix sûr : dans le doute, on
 * n'ouvre pas une console d'administration.
 */
export const AUTH_MODE_UNKNOWN: AuthMode = { oidc: false, armed: true };

/**
 * Lit une réponse de `/auth/mode` en la ramenant au contrat.
 * ⚠️ `armed` absent (déploiement antérieur au contrat, ou service tiers) est lu comme `true` — même
 * raisonnement fail-closed que ci-dessus, et ça rend le SPA déployable AVANT son serveur.
 */
export function normalizeAuthMode(raw: unknown): AuthMode {
  const r = (raw ?? {}) as Partial<AuthMode>;
  return { oidc: r.oidc === true, armed: r.armed !== false };
}
